import { initializeStore, SeamlessConnection, SeamlessCoreStore } from '@seamless/store';
import Logger from '@owc-utils/common/Logger';

declare type Listener = (state: any) => void;

declare type ListenerTree<T> = {
  [key: string]: ListenerNameSpace<T>;
};

declare type ListenerNameSpace<T> = {
  [key: string]: T[];
};

const listeners: ListenerTree<Listener> = {};

const initializedNamespaces = new Set(['OWC_COMPONENT_REGISTRY']);
export const store = initializeStore();

const addConnection = async (
  connStore: SeamlessCoreStore,
  connection: SeamlessConnection<Record<string, Function>>,
): Promise<void> => {
  return connStore.addConnection(connection).catch((e: Error) => Logger.error(e));
};

/**
 * Proxy method to connect to the redux store, under given namespace
 * @param connection
 * @returns Public dispatchers
 */
export const connect = async (connection: SeamlessConnection<Record<string, Function>>) => {
  if (!initializedNamespaces.has(connection.name)) {
    initializedNamespaces.add(connection.name);
    await addConnection(store, connection);
  }
  return store.getConnectionDispatchers(connection.name);
};

/**
 * Proxy method to subscribe to a given namespace, triggering the provided listener
 * @param namespace
 * @param listener
 * @throws if namespace is not yet registered
 */
export const subscribe = (id: string, namespace: string, listener: any) => {
  if (!initializedNamespaces.has(namespace)) {
    throw new Error(`[${id}] Cannot subscribe to namespace. ${namespace} has not been registered yet`);
  }

  const item = listeners[id];
  const namespaceItem = item && item[namespace];

  if (!item) {
    const payload: ListenerNameSpace<Listener> = {};
    payload[namespace] = [listener];
    listeners[id] = payload;
  } else if (!namespaceItem) {
    item[namespace] = [listener];
  } else {
    namespaceItem.push(listener);
  }

  return store.subscribe(namespace, listener);
};

/**
 * Proxy method to unsubscribe from a given namespace, triggering the provided listener
 * @param namespace
 * @param listener
 * @throws if namespace is not yet registered
 */
export const unsubscribe = (id: string, namespace: string, listener: any) => {
  if (!initializedNamespaces.has(namespace)) {
    throw new Error(`[${id}] Cannot subscribe to namespace. ${namespace} has not been registered yet`);
  }
  return store.unsubscribe(namespace, listener);
};

/**
 * Components should import getConnection if they need to pass data to
 * the store, rather than connecting multiple times to same namespace
 *
 */
export const getConnection = async <T extends Record<string, Function>>(namespace: string) => {
  return store.getConnectionDispatchers<T>(namespace);
};

export const handleDestroy = (id: string) => {
  const item = listeners[id];
  if (item) {
    const namespaces = Object.keys(item);
    namespaces.forEach(namespace => {
      item[namespace].forEach(listener => {
        store.unsubscribe(namespace, listener);
      });
    });
  }
};
